import type { PropertyFilters } from '@/types/property'

export type SearchEventParams = Record<string, string | number | boolean | undefined>

export function searchPerformedParams(input: {
  cityId?: number | null
  areaId?: number | null
  categoryId?: number | null
  intent?: string
  bhk?: string
  budgetMin?: number
  budgetMax?: number
  resultCount: number
  source: string
}): SearchEventParams {
  return {
    city_id: positiveId(input.cityId),
    area_id: positiveId(input.areaId),
    category_id: positiveId(input.categoryId),
    intent: input.intent,
    bhk: input.bhk,
    budget_min: input.budgetMin,
    budget_max: input.budgetMax,
    result_count: input.resultCount,
    source: input.source,
  }
}

export function filterAppliedParams(input: {
  cityId?: number | null
  areaId?: number | null
  categoryId?: number | null
  intent?: string
  bhk?: string
  budgetMin?: number
  budgetMax?: number
  source: string
}): SearchEventParams {
  return {
    city_id: positiveId(input.cityId),
    area_id: positiveId(input.areaId),
    category_id: positiveId(input.categoryId),
    intent: input.intent,
    bhk: input.bhk,
    budget_min: input.budgetMin,
    budget_max: input.budgetMax,
    source: input.source,
  }
}

export function sortAppliedParams(input: {
  sort: string
  cityId?: number | null
  source: string
}): SearchEventParams {
  return {
    sort: input.sort,
    city_id: positiveId(input.cityId),
    source: input.source,
  }
}

export function mapSearchParams(input: {
  cityId?: number | null
  resultCount: number
}): SearchEventParams {
  return {
    city_id: positiveId(input.cityId),
    result_count: input.resultCount,
  }
}

function positiveId(value: number | null | undefined): number | undefined {
  return value != null && value > 0 ? value : undefined
}

export interface NamedOptionId {
  id: number
  name: string
}

/** One selected value that matches exactly one loaded option id. Otherwise omit. */
export function resolveSingleOptionId(
  selected: string[] | undefined,
  options: readonly NamedOptionId[],
): number | undefined {
  if (!selected || selected.length !== 1 || options.length === 0) return undefined
  const raw = selected[0].trim()
  if (!raw) return undefined

  if (/^\d+$/.test(raw)) {
    const id = Number(raw)
    return options.some((option) => option.id === id && option.id > 0) ? id : undefined
  }

  const key = raw.toLowerCase()
  const matches = options.filter(
    (option) => option.id > 0 && option.name.trim().toLowerCase() === key,
  )
  return matches.length === 1 ? matches[0].id : undefined
}

/** Page 1 of a user search action. Pagination and later listing loads stay false. */
export function shouldAttachListingSearchLog(page: number, armed: boolean): boolean {
  return armed && page === 1
}

export type ListingChangeEvent = 'sort' | 'filter' | 'none'

/** Sort is not a filter. Budget slider movement is not a commit. */
export function listingChangeEvent(patch: Partial<PropertyFilters>): ListingChangeEvent {
  if (isSortPatch(patch)) return 'sort'
  if (isBudgetDrag(patch)) return 'none'
  return 'filter'
}

export function isBudgetDrag(patch: Partial<PropertyFilters>): boolean {
  const keys = Object.keys(patch)
  return keys.length > 0 && keys.every((key) => key === 'budgetMin' || key === 'budgetMax')
}

export function isSortPatch(patch: Partial<PropertyFilters>): boolean {
  return Object.keys(patch).length === 1 && patch.sort != null
}
